import { H2 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { Fire } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect, useRef, useState } from 'react';

import { POPULAR_ITEM_MINIMUM_SAVE_COUNT } from '@/components/ItemSave/ItemSave.constants';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryLotNumber, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSavedItemCountNum } from '@/redux/modules/savedItemCount';
import { useAppFormatMessage, useAppFormatMessageWithValues } from '@/utils/intl/useAppFormatMessage';

import { BiddingConsoleImageSlider } from './components/BiddingConsoleImageSlider/BiddingConsoleImageSlider';
import { CurrentItemDetails } from './components/CurrentItemDetails/CurrentItemDetails';

type Props = {
    itemId: number;
};

const SCROLL_THRESHOLD = 32;

export const ItemDetails = ({ itemId }: Props) => {
    const formatMessage = useAppFormatMessage();
    const formatMessageWithValues = useAppFormatMessageWithValues();
    const liveItemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const numberOfWatchers = useAppSelector((state) => getSavedItemCountNum(state, itemId));
    const liveItemLotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Latched in a ref so repeated scroll events see stable state without extra renders: a variable inside the callback would
    // reset on every invocation, and useState would re-render for each scroll tick.
    const hasScrolledPastTopHintRef = useRef(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        const { clientHeight, scrollHeight, scrollTop } = container;
        const isScrollable = scrollHeight > clientHeight;
        const isAtTop = scrollTop <= SCROLL_THRESHOLD;

        if (scrollTop > SCROLL_THRESHOLD) {
            hasScrolledPastTopHintRef.current = true;
        }

        setShowTooltip(isScrollable && isAtTop && !hasScrolledPastTopHintRef.current);
        setIsAtBottom(!isScrollable || scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD);
    }, []);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        container.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    return (
        <div className="relative flex min-h-0 flex-1 flex-col bg-background-primary">
            <div
                ref={scrollContainerRef}
                className="flex min-h-0 flex-1 flex-col gap-sm overflow-y-auto px-md py-sm"
            >
                <div>
                    <div className="flex items-center justify-between gap-sm">
                        <H2 className="line-clamp-2">
                            {formatMessageWithValues(
                                'bidding_console',
                                { lotNumber: liveItemLotNumber, title: liveItemTitle },
                                'console',
                                'itemTitle'
                            )}
                        </H2>
                    </div>
                    {numberOfWatchers > 0 && (
                        <div className="mt-2xs flex items-center gap-2xs">
                            {numberOfWatchers >= POPULAR_ITEM_MINIMUM_SAVE_COUNT && (
                                <Fire
                                    color="critical"
                                    size="sm"
                                    style="fill"
                                />
                            )}

                            <BodySecondary>
                                {formatMessageWithValues(
                                    'bidding_console',
                                    { count: numberOfWatchers },
                                    'console',
                                    'watchers'
                                )}
                            </BodySecondary>
                        </div>
                    )}
                </div>
                <BiddingConsoleImageSlider itemId={itemId} />
                <CurrentItemDetails itemId={itemId} />
                {showTooltip && (
                    <div className="sticky -bottom-sm z-10 flex justify-center py-xs">
                        <div className="w-[203px] rounded-md bg-background-primary px-sm py-xs shadow-02">
                            <BodySecondary className="text-text-primary">
                                {formatMessage('bidding_console', 'console', 'scrollForMoreDetails')}
                            </BodySecondary>
                        </div>
                    </div>
                )}
            </div>
            {!isAtBottom && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-2xl bg-gradient-to-t from-background-primary to-transparent"
                />
            )}
        </div>
    );
};
